import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import CustomerModal from '../components/CustomerModal';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const searchInputRef = React.useRef(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // ή 'edit'

  useEffect(() => {
    fetch('https://api.remindly.gr/get-customers.php', {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        setCustomers(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('❌ Σφάλμα πελατών:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setSearchTerm('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const normalizeGreek = (text) =>
    text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const filteredCustomers = customers.filter((c) =>
    normalizeGreek(c.name).includes(normalizeGreek(searchTerm)) ||
    normalizeGreek(c.phone || '').includes(normalizeGreek(searchTerm)) ||
    normalizeGreek(c.email || '').includes(normalizeGreek(searchTerm))
  );

  const handleSave = async (custData) => {
    try {
      const res = await fetch('https://api.remindly.gr/add-customer.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(custData),
      });

      const data = await res.json();
      if (data.success) {
        setCustomers((prev) => [...prev, { id: data.id, ...custData }]);
        toast.success('Ο πελάτης προστέθηκε!');
        setModalOpen(false);
        setSelectedCustomer(null);
        setModalMode('add');
      } else {
        toast.error(data.error || 'Σφάλμα κατά την αποθήκευση.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Αποτυχία σύνδεσης με τον server.');
    }
  };

  const handleUpdate = async (updatedCustomer) => {
    try {
      const res = await fetch('https://api.remindly.gr/update-customer.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedCustomer),
      });

      const data = await res.json();
      if (data.success) {
        setCustomers((prev) =>
          prev.map((cust) =>
            cust.id === updatedCustomer.id ? { ...cust, ...updatedCustomer } : cust
          )
        );
        toast.success('Ο πελάτης ενημερώθηκε!');
        setModalOpen(false);
        setSelectedCustomer(null);
        setModalMode('add');
      } else {
        toast.error(data.error || 'Σφάλμα κατά την ενημέρωση.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Αποτυχία σύνδεσης με τον server.');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch('https://api.remindly.gr/delete-customer.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });

      const data = await res.json();
      if (data.success) {
        setCustomers((prev) => prev.filter((c) => c.id !== id));
        toast.success('Ο πελάτης διαγράφηκε.');
      } else {
        toast.error(data.error || 'Σφάλμα κατά τη διαγραφή.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Αποτυχία σύνδεσης με τον server.');
    }
  };

  return (
    <div className="p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h2 className="text-xl font-semibold">Πελάτες</h2>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Αναζήτηση πελάτη."
              ref={searchInputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 pl-3 pr-8 py-2 rounded w-full text-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-lg font-bold focus:outline-none"
                aria-label="Καθαρισμός"
              >
                ×
              </button>
            )}
          </div>

          <button
            onClick={() => {
              setSelectedCustomer(null);
              setModalMode('add');
              setModalOpen(true);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 whitespace-nowrap"
          >
            + Νέος Πελάτης
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Φόρτωση πελατών.</p>
      ) : (
        <div className="flex flex-col space-y-4">
          {filteredCustomers.map((c) => (
            <div
              key={c.id}
              className="bg-white shadow border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
                <p className="font-medium text-sm sm:text-base">
                  {c.name}
                  {c.phone && (
                    <span className="ml-2 text-sm text-gray-600">· {c.phone}</span>
                  )}
                </p>
                {c.email && (
                  <p className="text-sm text-gray-500 sm:ml-4 sm:mt-0 mt-1">
                    {c.email}
                  </p>
                )}
              </div>
              <div className="mt-2 sm:mt-0">
                <button
                  onClick={() => {
                    console.log('🧾 selected customer:', c);
                    setSelectedCustomer(c);
                    setModalMode('edit');
                    setModalOpen(true);
                  }}
                  className="text-indigo-600 hover:underline text-sm"
                >
                  ✏️ Επεξεργασία
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CustomerModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCustomer(null);
          setModalMode('add');
        }}
        onSave={handleSave}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        existingCustomers={customers}
        mode={modalMode}
        customer={selectedCustomer}
      />
    </div>
  );
};

export default Customers;
